import upperFirst from "lodash/upperFirst";
import queryString from "query-string";

import type { ISwSettings } from "app/@types/ISwSettings";
import { swSettings } from "app/common/services/swSettings";
import { minAbbrNumberFilter } from "app/filters/ngFilters";
import type { ActivationObjectSchema } from "app/pages/activation/types/api";
import { getShareValue, getVolumeValue } from "app/pages/website-analysis/traffic-sources/search/common/utils";
import type { IDurationData } from "app/services/DurationService";
import DurationService from "app/services/DurationService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { DEFAULT_CONFIG_ID } from "./consts";
import { ACITAVION_SET_LEADER_FIELD } from "./MotinorKeywordsGroupTableSettings";
import type { IKeywordGroupDetail } from "./types";

interface IRequestBody {
    country: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    KeywordsGroup: string;
    sitesFilterExact?: string[];
}

const getUrlForKeyWord = (keyword: string, country: string, keywordOverviewState: string): string => {
    const state = proNavigator.getState(keywordOverviewState);
    const configId = proNavigator.getConfigId(state);
    const defaultParams = (swSettings as ISwSettings).components[configId].defaultParams;
    return proNavigator.href(keywordOverviewState, {
        ...defaultParams,
        keyword,
        country,
    });
};

export const transformData = (country: string, keywordOverviewState?: string) => (data) => {
    return {
        ...data,
        records: data.data.map((keywordGroupDetail: IKeywordGroupDetail) => {
            const {
                volume,
                volumeTrend,
                primaryIntent,
                secondaryIntent,
                keyword,
                totalClicksShare,
                totalClicks,
                clicksShareOrganic,
                clicksSharePaid,
                filteredLeadingDomain,
                filteredLeadingDomainClicks,
                ...rest
            } = keywordGroupDetail;
            return {
                ...rest,
                keyword,
                country,
                volume: getVolumeValue(volume),
                volumeTrends: volumeTrend ?? [],
                intent: {
                    PrimaryIntent: upperFirst(primaryIntent),
                    SecondaryIntent: upperFirst(secondaryIntent),
                },
                url: keywordOverviewState ? getUrlForKeyWord(keyword, country, keywordOverviewState) : undefined,
                totalClicksShare: getShareValue(totalClicksShare),
                totalClicks: getVolumeValue(totalClicks),
                Organic: clicksShareOrganic,
                Paid: clicksSharePaid,
                // the cell component needs the data in the following format:
                // clicksShareOrganic -- as the primary value.
                // Organic and Paid -- as the row values.
                clicksShareOrganic,
                [ACITAVION_SET_LEADER_FIELD]: {
                    domain: filteredLeadingDomain,
                    clicks: minAbbrNumberFilter()(getVolumeValue(filteredLeadingDomainClicks), 50),
                },
            };
        }),
        totalRecords: data.totalCount,
    };
};

const getDurationForApi = (duration: string): Pick<IDurationData["forAPI"], "from" | "to" | "isWindow"> => {
    const { from, to, isWindow } = DurationService.getDurationData(duration, "", DEFAULT_CONFIG_ID).forAPI;
    return { from, to, isWindow };
};

export const getApiParamsForTable = (
    activationSets: ActivationObjectSchema[],
    sorter: { sort: string; asc: boolean },
    params: Record<string, string>,
): { initialFilters: Record<string, string>; requestBody: IRequestBody } => {
    const { keywordGroupId, country, webSource, duration, sites, isWWW, keywordsType, activationSetId, groupHash } =
        params;
    const benchmarkToArena = !!params.sites?.split(",").length;
    const { from, to, isWindow } = getDurationForApi(duration);
    let initialFilters;
    let requestBody;

    const baseFilters = {
        country,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        KeywordsGroup: keywordGroupId,
    };

    if (benchmarkToArena) {
        initialFilters = {
            ...baseFilters,
            asc: sorter.asc,
            groupHash,
            from,
            to,
            isWindow,
            includeSubDomains: isWWW === "*",
            /* eslint-disable @typescript-eslint/naming-convention */
            KeywordSource: keywordsType,
            WebSource: webSource,
            ApplyIntervalToVolume: true,
            UseVolume2: true,
            Keys: sites,
            /* eslint-enable @typescript-eslint/naming-convention */
        };
    } else {
        initialFilters = {
            ...sorter,
            groupHash,
        };
    }

    if (!benchmarkToArena) {
        requestBody = {
            ...baseFilters,
            ...(activationSetId
                ? { sitesFilterExact: activationSets?.find((set) => set.id === activationSetId)?.allDomains }
                : {}),
        };
    }
    return { initialFilters, requestBody };
};

export const getExcelConfig = (
    activationSets: ActivationObjectSchema[],
    sorter: { sort: string; asc: boolean },
    paramsForRequest: Record<string, string>,
    filter: string | undefined,
    excelEndpoint: string,
): {
    url: string;
    requestBody: IRequestBody;
} => {
    const { keywordGroupId, country, activationSetId } = paramsForRequest;

    const url = `${excelEndpoint}?${queryString.stringify({ ...sorter, ...(filter && { filter }) })}`;
    const requestBody = {
        country,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        KeywordsGroup: keywordGroupId,
        ...(activationSetId
            ? { sitesFilterExact: activationSets?.find((set) => set.id === activationSetId)?.allDomains }
            : {}),
    };

    return { url, requestBody };
};

export const getListWithoutDuplications = (currentList: string[], newList: string[]): string[] => [
    ...new Set([...currentList, ...newList]),
];
